// EditUserLevelModal.tsx — Co Marca — Editar Nivel de Usuario
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Shield, CheckCircle2 } from 'lucide-react';
import { updateUserLevel } from '../../services/userLevelService';

interface UserLevel { id: number; nombre: string; perfil_asociado: 'cliente'|'personal'; activo: boolean; }
interface EditUserLevelModalProps {
    isOpen: boolean; onClose: () => void;
    userLevel: UserLevel | null;
    onSave: (id: number, data: { nombre: string; activo: boolean; perfil_asociado: 'cliente'|'personal' }) => void;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.eulm-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes eulmFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes eulmSlideUp{from{opacity:0;transform:translateY(24px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.eulm-overlay{animation:eulmFadeIn .22s ease both;}
.eulm-dialog{animation:eulmSlideUp .32s cubic-bezier(.16,1,.3,1) both;}
.eulm-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fafafa;font-size:.88rem;color:#374151;outline:none;transition:all .2s;}
.eulm-input:focus{border-color:${colors.secondary};box-shadow:0 0 0 3px ${colors.secondary}12;background:#fff;}
.eulm-select{appearance:none;}
.eulm-btn-primary{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.secondary},#004d7a);color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(97, 37, 123,.28);transition:all .22s cubic-bezier(.16,1,.3,1);}
.eulm-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(97, 37, 123,.34);}
.eulm-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.eulm-btn-ghost{display:inline-flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;color:#374151;border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;}
.eulm-btn-ghost:hover{background:#f1f5f9;}
.custom-scroll::-webkit-scrollbar{width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background:rgba(97, 37, 123,.5);}
`;

const EditUserLevelModal: React.FC<EditUserLevelModalProps> = ({ isOpen, onClose, userLevel, onSave }) => {
    const [nombre, setNombre]                 = useState('');
    const [activo, setActivo]                 = useState(true);
    const [perfilAsociado, setPerfilAsociado] = useState<'cliente'|'personal'>('cliente');
    const [isLoading, setIsLoading]           = useState(false);

    useEffect(() => {
        if (userLevel) { setNombre(userLevel.nombre); setActivo(userLevel.activo); setPerfilAsociado(userLevel.perfil_asociado); }
    }, [userLevel]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userLevel) return;
        setIsLoading(true);
        try {
            await updateUserLevel(userLevel.id, { nombre, activo, perfil_asociado: perfilAsociado });
            onSave(userLevel.id, { nombre, activo, perfil_asociado: perfilAsociado });
            onClose();
        } catch (err) { console.error('Error actualizando nivel:', err); }
        finally { setIsLoading(false); }
    };

    if (!isOpen || !userLevel) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="eulm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="eulm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>
                <div className="eulm-dialog custom-scroll" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'480px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid #f1f5f9' }}>

                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.secondary},${colors.secondaryLight},${colors.accentDark},${colors.primary})`, borderRadius:'24px 24px 0 0' }}/>

                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${colors.secondary}30` }}>
                                    <Edit size={17} color="#fff"/>
                                </div>
                                <div>
                                    <h3 style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Editar Nivel</h3>
                                    <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Modificando <strong style={{ color:'#374151' }}>{userLevel.nombre}</strong></p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.secondary,colors.secondaryLight,colors.accentDark,colors.primary].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={15} color="#64748b"/>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}>
                        <div>
                            <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                <Shield size={11} color={colors.secondary}/> Nombre *
                            </label>
                            <input type="text" required value={nombre} onChange={e=>setNombre(e.target.value)} className="eulm-input" placeholder="Nombre del nivel"/>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Perfil asociado</label>
                            <select value={perfilAsociado} onChange={e=>setPerfilAsociado(e.target.value as any)} className="eulm-input eulm-select">
                                <option value="cliente">Cliente</option>
                                <option value="personal">Personal</option>
                            </select>
                        </div>
                        <div style={{ background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                            <div>
                                <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a', marginBottom:'2px' }}>Estado del nivel</p>
                                <p style={{ fontSize:'.7rem', color:activo?colors.secondaryLight:'#94a3b8', fontWeight:'600' }}>
                                    {activo?'● Visible para usuarios':'○ Oculto para usuarios'}
                                </p>
                            </div>
                            <div onClick={()=>setActivo(!activo)} style={{ width:'44px', height:'24px', borderRadius:'100px', background:activo?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                <div style={{ position:'absolute', top:'3px', left:activo?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                            </div>
                        </div>
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="eulm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={isLoading} className="eulm-btn-primary">
                                {isLoading ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</> : <><CheckCircle2 size={14}/> Guardar Cambios</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default EditUserLevelModal;